/**
 * Telefonun verdiği görseli sunucunun kabul ettiği biçime ve makul bir boyuta hazırlar.
 *
 * Telefon kameraları 12-200 MP fotoğraf üretir (5-20 MB). Bu boyut mobil bağlantıda dakikalarca
 * yüklenir ve "yükleme başarısız" hatasına yol açar. Fişin okunması için en uzun kenarı 3000 px
 * yeterlidir; bu yüzden büyük görseller telefonda JPEG'e küçültülür (yön bilgisi uygulanır).
 *  - En uzun kenarı 3000 px'i ve boyutu 3 MB'ı aşmayan JPEG/PNG olduğu gibi kalır.
 *  - HEIC (iPhone) olduğu gibi gönderilir; sunucu dönüştürür. 15 MB'ı aşarsa hata verilir.
 *  - WEBP/AVIF/GIF ya da türü boş gelen dosyalar (bazı Android galerileri) JPEG'e çevrilir.
 */
const KEEP_TYPES = ['image/jpeg', 'image/png'];
const HEIC_TYPES = ['image/heic', 'image/heif'];
const MAX_BYTES = 15 * 1024 * 1024;
const KEEP_BYTES = 3 * 1024 * 1024;
const MAX_SIDE = 3000;

export async function prepareImage(file: File): Promise<{ blob: Blob; name: string }> {
  const type = file.type || typeFromName(file.name);
  const base = (file.name || 'fis').replace(/\.[^.]+$/, '');
  if (HEIC_TYPES.includes(type)) {
    if (file.size > MAX_BYTES) throw new Error('HEIC görseli 15 MB sınırını aşıyor. Lütfen fotoğrafı yeniden çekin.');
    return { blob: type === file.type ? file : new Blob([file], { type }), name: file.name || 'fis.heic' };
  }
  const source = await decode(file);
  try {
    const { w, h } = sizeOf(source);
    if (KEEP_TYPES.includes(type) && file.size <= KEEP_BYTES && Math.max(w, h) <= MAX_SIDE) {
      return { blob: type === file.type ? file : new Blob([file], { type }), name: file.name || `fis.${type === 'image/png' ? 'png' : 'jpg'}` };
    }
    return { blob: await toJpeg(source, w, h), name: `${base}.jpg` };
  } finally {
    if ('close' in source) source.close();
  }
}

function typeFromName(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return ({ jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', heic: 'image/heic', heif: 'image/heif', webp: 'image/webp' } as Record<string, string>)[ext] ?? '';
}

type Source = ImageBitmap | HTMLImageElement;

async function decode(file: Blob): Promise<Source> {
  try {
    // EXIF yönü uygulanır; dik çekilen fiş yan dönmez
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return loadImage(file);
  }
}

function sizeOf(source: Source): { w: number; h: number } {
  return 'naturalWidth' in source ? { w: source.naturalWidth, h: source.naturalHeight } : { w: source.width, h: source.height };
}

async function toJpeg(source: Source, w: number, h: number): Promise<Blob> {
  const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Görsel işlenemedi.');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  for (const quality of [0.88, 0.8, 0.7]) {
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', quality));
    if (blob && blob.size <= KEEP_BYTES * 2) return blob;
  }
  throw new Error('Görsel işlenemedi; lütfen fotoğrafı yeniden çekin.');
}

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Bu dosya bir görsel olarak açılamadı. JPEG, PNG veya HEIC seçin.')); };
    img.src = url;
  });
}
